import { useState, useEffect, useCallback } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useAuthStore, useIsInternal } from '@/stores/authStore';
import { useIsMobile } from '@/hooks/useIsMobile';
import { QSidebar } from './QSidebar';
import { QHeader } from './QHeader';
import { CommandPalette } from './CommandPalette';
import { ErrorBoundary } from '@/components/common';
import { InstallBanner } from '@/components/common/InstallBanner';
import { OfflineBanner } from '@/components/common/OfflineBanner';
import { MobileLayout } from './mobile';
import { cn } from '@/lib/utils';
import { useWebSocket } from '@/hooks/useWebSocket';
import { useNotificacionesPush } from '@/hooks/useNotificacionesPush';
import { useNotificacionCount } from '@/hooks/queries/useNotificaciones';
import { useSessionManager } from '@/hooks/useSessionManager';
import { SessionWarningDialog } from '@/components/common/SessionWarningDialog';

const SIDEBAR_COLLAPSED_KEY = 'qops-sidebar-collapsed';

export default function MainLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false); // mobile only
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true';
    } catch {
      return false;
    }
  });
  const [commandOpen, setCommandOpen] = useState(false);

  const { user, logout } = useAuthStore();
  const isInternal = useIsInternal();
  const isMobile = useIsMobile();
  const navigate = useNavigate();

  // Session management: proactive refresh + idle detection
  const { showWarning, countdownSeconds, dismissWarning } = useSessionManager();

  // WebSocket for real-time updates
  const { isConnected, onlineAgents, lastEvent } = useWebSocket();
  useNotificacionesPush(lastEvent);

  // Real notification count from API (invalidated by WS events)
  const { data: notifData } = useNotificacionCount();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(next));
      } catch {
        // localStorage unavailable
      }
      return next;
    });
  }, []);

  // Global Cmd+K / Ctrl+K listener (desktop only)
  useEffect(() => {
    if (isMobile) return;
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setCommandOpen((prev) => !prev);
      }
    };
    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, [isMobile]);

  const notificationCount = notifData?.total ?? 0;

  // ── Mobile Layout ──
  if (isMobile) {
    return (
      <MobileLayout
        notificationCount={notificationCount}
        showWarning={showWarning}
        countdownSeconds={countdownSeconds}
        onDismissWarning={dismissWarning}
        onLogout={handleLogout}
      />
    );
  }

  // ── Desktop Layout ──
  return (
    <div className="min-h-screen bg-background">
      {/* Offline banner — non-fixed, pushes content down */}
      <OfflineBanner />

      <QSidebar
        collapsed={collapsed}
        onToggle={toggleCollapsed}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        isInternal={isInternal}
      />

      {/* Main content area */}
      <div
        className={cn(
          'min-h-screen transition-all duration-200',
          collapsed ? 'lg:ml-16' : 'lg:ml-64'
        )}
      >
        <QHeader
          user={user}
          onMobileMenuToggle={() => setSidebarOpen(true)}
          onLogout={handleLogout}
          onOpenCommandPalette={() => setCommandOpen(true)}
          notificationCount={notificationCount}
          onlineAgentCount={onlineAgents.length}
          isWsConnected={isConnected}
        />

        {/* Page content */}
        <main className="p-4 sm:p-6">
          <ErrorBoundary>
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>

      {/* Command Palette — global */}
      <CommandPalette
        open={commandOpen}
        onOpenChange={setCommandOpen}
        isInternal={isInternal}
      />

      {/* Session idle warning */}
      <SessionWarningDialog
        open={showWarning}
        countdownSeconds={countdownSeconds}
        onContinue={dismissWarning}
      />

      {/* PWA install banner — fixed bottom */}
      <InstallBanner />
    </div>
  );
}
